import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { getGroups, Group } from '../../api/groupsApi';
import Layout from '../../components/Layout';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import TableHeader from '../../components/TableHeader';
import SearchFilter from '../../components/SearchFilter';
import { sortData, filterData, SortState } from '../../utils/tableUtils';
import Table, { TableColumn } from '../../components/Table';

// Import missing API functions that we'll create next
import { createGroup, updateGroup, deleteGroup } from '../../api/groupsApi';

interface GroupFormData {
  id: number | string;
  name: string;
}

const Groups: React.FC = () => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [displayedGroups, setDisplayedGroups] = useState<Group[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [formData, setFormData] = useState<GroupFormData>({
    id: '',
    name: '',
  });
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortState, setSortState] = useState<SortState<Group>>({
    key: 'id',
    direction: 'asc'
  });

  // Fetch groups on component mount
  useEffect(() => {
    fetchGroups();
  }, []);

  // Apply sorting and filtering whenever the base data, search term, or sort state changes
  useEffect(() => {
    let filteredData = groups;
    
    // Apply search filtering
    if (searchTerm) {
      filteredData = filterData(
        groups, 
        searchTerm, 
        ['id', 'name']
      );
    }
    
    // Apply sorting
    filteredData = sortData(filteredData, sortState.key, sortState.direction);
    
    setDisplayedGroups(filteredData);
  }, [groups, searchTerm, sortState]);

  const fetchGroups = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const groupsResponse = await getGroups().catch(() => []);
      setGroups(groupsResponse);
    } catch (err) {
      console.error('Error fetching groups:', err);
      setError('Failed to load groups. Please try again.');
      setGroups([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle sort column click
  const handleSort = (key: keyof Group) => {
    setSortState(prevState => ({
      key,
      direction: prevState.key === key && prevState.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const openModal = (group: Group | null = null): void => {
    if (group) {
      // Edit mode
      setEditingGroup(group);
      setFormData({
        id: group.id,
        name: group.name,
      });
    } else {
      // Create mode
      setEditingGroup(null);
      setFormData({
        id: '',
        name: '',
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = (): void => {
    setIsModalOpen(false);
    setError('');
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    // Convert id to number if it contains only digits
    if (name === 'id' && value !== '' && /^\d+$/.test(value)) {
      setFormData({
        ...formData,
        [name]: parseInt(value, 10)
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const validateForm = (): boolean => {
    if (!formData.name) {
      setError('Group name is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      if (editingGroup) {
        // Update existing group
        await updateGroup(Number(editingGroup.id), { name: formData.name });
      } else {
        // Create new group
        await createGroup({ name: formData.name });
      }
      
      // Refresh groups list
      fetchGroups();
      closeModal();
    } catch (err) {
      console.error('Error saving group:', err);
      setError('Failed to save group. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: number): Promise<void> => {
    if (window.confirm('Are you sure you want to delete this group?')) {
      setIsLoading(true);
      try {
        await deleteGroup(id);
        fetchGroups();
      } catch (err) {
        console.error('Error deleting group:', err);
        setError('Failed to delete group. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Define table columns
  const columns: TableColumn<Group>[] = [
    {
      key: 'id',
      title: 'ID',
      render: (group) => <span className="text-gray-400">{group.id}</span>,
      sortable: true
    },
    {
      key: 'name',
      title: 'Name',
      render: (group) => <span className="text-white">{group.name}</span>,
      sortable: true
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (group) => (
        <div className="flex space-x-2">
          <button 
            className="text-stake-blue hover:text-blue-300 p-1 rounded hover:bg-dark-200 transition-colors"
            onClick={() => openModal(group)}
          >
            <PencilIcon className="h-5 w-5" />
          </button>
          <button 
            className="text-stake-red hover:text-red-400 p-1 rounded hover:bg-dark-200 transition-colors"
            onClick={() => handleDelete(group.id)}
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>
      )
    }
  ];

  return (
    <Layout>
      <div className="-mx-3 sm:mx-0 sm:px-4 py-4">
        <div className="flex justify-between items-center mb-6 px-3 sm:px-0">
          <div className="flex items-center space-x-4">
            <SearchFilter
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search groups..."
              className="w-64"
            />
            <button
              onClick={() => openModal()}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Group
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-stake-red bg-opacity-20 border border-stake-red text-white px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <Table<Group>
          fullWidth
          compact
          data={displayedGroups}
          columns={columns}
          keyExtractor={(group) => group.id}
          emptyMessage="No groups found"
          isLoading={isLoading && !isModalOpen}
          sortState={sortState}
          onSort={handleSort}
          searchTerm={searchTerm}
        />

        {/* Modal for add/edit group */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-10">
            <div className="bg-dark-100 rounded-lg overflow-hidden shadow-xl transform w-full max-w-lg">
              <form onSubmit={handleSubmit}>
                <div className="px-6 py-4">
                  <div className="text-lg font-medium text-white mb-4">
                    {editingGroup ? 'Edit Group' : 'Add New Group'}
                  </div>

                  {error && (
                    <div className="bg-stake-red bg-opacity-20 border border-stake-red text-white px-4 py-3 rounded mb-4">
                      {error}
                    </div>
                  )}

                  <div className="mb-4">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-300">
                      Group Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="mt-1 block w-full bg-dark-200 border-stake-border text-gray-300 rounded-md shadow-sm focus:ring-stake-blue focus:border-stake-blue sm:text-sm"
                      required
                    />
                  </div>
                </div>

                <div className="bg-dark-200 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-stake-blue text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-stake-blue sm:ml-3 sm:w-auto sm:text-sm"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-stake-border shadow-sm px-4 py-2 bg-dark-300 text-base font-medium text-gray-300 hover:bg-dark-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-stake-blue sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Groups; 